if (!customElements.get("deployer-cart-discount")) {
  customElements.define(
    "deployer-cart-discount",
    class DeployerCartDiscount extends HTMLElement {
      constructor() {
        super();

        this.applyButton = this.querySelector(".deployer-cart-discount_button");
        this.input = this.querySelector(".deployer-cart-discount_input");
        this.removeButton = this.querySelector(".cart-discount__remove-discount");
        this.abortController = null;

        if (!this.applyButton || !this.input) {
          console.warn("[deployer-cart-discount] Bouton ou input introuvable.");
          return;
        }

        this.applyButton.addEventListener("click", this.submitDiscount.bind(this));
        this.removeButton?.addEventListener("click", this.removeDiscount.bind(this));
        this.addEventListener("discount:remove", this.removeDiscount.bind(this));

        this.syncLocalStorageWithCart();
        this.updateInputDisplay();
      }

      async submitDiscount(event) {
        event.preventDefault();

        const discountCode = this.input.value.trim();

        try {
          const cart = await this.getCart();
          const activeDiscounts = this.getActiveDiscountCodes(cart);

          if (activeDiscounts.length > 0) {
            this.input.value = "";
            await this.removeDiscount();
            return;
          }

          if (!discountCode) {
            this.setErrorMode("Veuillez saisir un code");
            return;
          }

          await this.applyCode(discountCode);
        } catch (error) {
          console.error("Erreur lors de la soumission du code promo:", error);
          this.setErrorMode("Une erreur est survenue");
        }
      }

      async removeDiscount() {
        const badge = this.querySelector(".cart-discount_badge");

        if (badge?.classList.contains("loading")) return;

        this.setButtonLoading();
        badge?.classList.add("loading");

        try {
          await this.applyCode("");
        } catch (error) {
          console.error("Erreur lors de la suppression du code:", error);
          this.setErrorMode("Une erreur est survenue");
        } finally {
          this.setButtonLoaded();
          badge?.classList.remove("loading");
        }
      }

      async applyCode(discountCode) {
        this.setButtonLoading();

        if (this.abortController) {
          this.abortController.abort();
        }

        this.abortController = new AbortController();

        try {
          const cartData = await this.getCart(this.abortController.signal);
          const existingDiscounts = this.getActiveDiscountCodes(cartData);

          let finalDiscounts = [];

          if (discountCode) {
            const codeAlreadyApplied = existingDiscounts.some(
              (code) => code.toUpperCase() === discountCode.toUpperCase()
            );

            if (codeAlreadyApplied) {
              this.setErrorMode("Ce code promo est déjà appliqué");
              return;
            }

            finalDiscounts = [...existingDiscounts, discountCode];
          }

          const response = await fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              discount: finalDiscounts.join(","),
              attributes: {
                discount: discountCode || null,
              },
              sections: window.deployerAdapter?.cartDrawerSection || "cart-drawer",
              sections_url: window.location.pathname,
            }),
            signal: this.abortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
          }

          const updatedCart = await response.json();

          if (discountCode) {
            const failedDiscount = updatedCart.discount_codes?.find(
              (discount) =>
                discount.code?.toUpperCase() === discountCode.toUpperCase() &&
                !discount.applicable
            );

            if (failedDiscount) {
              this.removeStoredDiscount();
              this.setErrorMode("Ce code promo n'est pas applicable");
              await this.updateCartAttribute(null);
              return;
            }

            const appliedDiscount = updatedCart.discount_codes?.find(
              (discount) =>
                discount.code?.toUpperCase() === discountCode.toUpperCase() &&
                discount.applicable
            );

            if (!appliedDiscount) {
              this.removeStoredDiscount();
              this.setErrorMode("Ce code promo n'est pas valide");
              await this.updateCartAttribute(null);
              return;
            }

            this.storeDiscount(appliedDiscount.code.trim(), updatedCart.total_price || 0);
          } else {
            this.removeStoredDiscount();
          }

          this.updateInputDisplay();
          this.refreshCart(updatedCart);
        } catch (error) {
          if (error.name === "AbortError") return;

          console.error("Erreur applyCode:", error);
          this.setErrorMode("Une erreur est survenue");
        } finally {
          this.setButtonLoaded();
        }
      }

      async getCart(signal = null) {
        const response = await fetch(window.Shopify.routes.root + "cart.js", {
          signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Erreur HTTP cart.js: ${response.status}`);
        }

        return await response.json();
      }

      getActiveDiscountCodes(cart) {
        return (
          cart.discount_codes
            ?.filter((discount) => discount.applicable)
            .map((discount) => discount.code)
            .filter(Boolean) || []
        );
      }

      async syncLocalStorageWithCart() {
        try {
          const cart = await this.getCart();
          const activeDiscounts = this.getActiveDiscountCodes(cart);

          if (activeDiscounts.length > 0) {
            this.storeDiscount(activeDiscounts[0], cart.total_price || 0);
          } else {
            this.removeStoredDiscount();
          }

          this.updateInputDisplay();
        } catch (error) {
          console.error("Erreur lors de la synchronisation du code promo:", error);
        }
      }

      async updateCartAttribute(discount = null) {
        try {
          const response = await fetch(window.Shopify.routes.root + "cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              attributes: {
                discount,
              },
              sections: window.deployerAdapter?.cartDrawerSection || "cart-drawer",
              sections_url: window.location.pathname,
            }),
          });

          if (!response.ok) {
            throw new Error(`Erreur HTTP updateCartAttribute: ${response.status}`);
          }

          const data = await response.json();
          this.refreshCart(data);
        } catch (error) {
          console.error("Erreur lors de la mise à jour des attributs du panier:", error);
        }
      }

      storeDiscount(code, totalCart = 0) {
        localStorage.setItem(
          "discountCode",
          JSON.stringify({
            code,
            totalCart,
          })
        );
      }

      removeStoredDiscount() {
        localStorage.removeItem("discountCode");
      }

      getStoredDiscount() {
        try {
          const storedDiscount = localStorage.getItem("discountCode");

          if (!storedDiscount) return null;

          return JSON.parse(storedDiscount);
        } catch (error) {
          console.warn("discountCode invalide dans localStorage. Suppression.");
          this.removeStoredDiscount();
          return null;
        }
      }

      updateInputDisplay() {
        const storedDiscount = this.getStoredDiscount();
        const hasDiscountCode = Boolean(storedDiscount?.code);

        if (hasDiscountCode) {
          this.input.placeholder = "";
          this.input.value = storedDiscount.code;
        } else {
          this.input.placeholder = this.input.dataset.defaultPlaceholder || "";
          this.input.value = "";
        }
      }

      setErrorMode(message = "Veuillez saisir un code valide") {
        this.setButtonLoaded();

        const currentPlaceholder = this.input.placeholder;

        this.input.focus();
        this.input.classList.add("deployer-cart-discount_input-error");
        this.input.placeholder = message;
        this.input.value = "";

        setTimeout(() => {
          this.input.classList.remove("deployer-cart-discount_input-error");
          this.input.placeholder = currentPlaceholder;
          this.updateInputDisplay();
        }, 2000);
      }

      setButtonLoading() {
        this.applyButton.disabled = true;
        this.applyButton.classList.add("deployer-button--is-loading");
      }

      setButtonLoaded() {
        this.applyButton.disabled = false;
        this.applyButton.classList.remove("deployer-button--is-loading");
      }

      refreshCart(cartData = {}) {
        document.dispatchEvent(new CustomEvent("deployer-cart-drawer:refresh"));

        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-drawer",
            cartData,
          });
        }

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cartData,
            },
          })
        );

        if (window.location.pathname.includes("/cart")) {
          window.location.reload();
        }
      }
    }
  );
}